/**
 * TemplateDesigner — 主设计器组件
 *
 * Three-panel layout: ElementPalette (left) | DesignCanvas (center) | PropertyPanel (right)
 * Top toolbar with template controls.
 * State managed via useReducer.
 */

import { useReducer, useCallback, useMemo } from 'react';
import {
  type DesignTemplate,
  type DesignBand,
  type DesignElement,
  type DesignerAction,
  createDefaultTemplate,
} from './types';
import ElementPalette from './ElementPalette';
import DesignCanvas from './DesignCanvas';
import PropertyPanel from './PropertyPanel';
import {
  Save,
  Eye,
  Undo2,
  FileText,
  PanelLeftClose,
  PanelLeftOpen,
  PanelRightClose,
  PanelRightOpen,
  Download,
  Upload,
} from 'lucide-react';
import { useState } from 'react';

// ── State ────────────────────────────────────────────────────

interface DesignerState {
  template: DesignTemplate;
  selectedBandId: string | null;
  selectedElementId: string | null;
  history: DesignTemplate[];
  historyIndex: number;
}

function createInitialState(initial?: DesignTemplate): DesignerState {
  const t = initial ?? createDefaultTemplate();
  return {
    template: t,
    selectedBandId: null,
    selectedElementId: null,
    history: [structuredClone(t)],
    historyIndex: 0,
  };
}

function pushHistory(state: DesignerState): DesignerState {
  const newHistory = state.history.slice(0, state.historyIndex + 1);
  newHistory.push(structuredClone(state.template));
  return { ...state, history: newHistory, historyIndex: newHistory.length - 1 };
}

function designerReducer(state: DesignerState, action: DesignerAction): DesignerState {
  switch (action.type) {
    case 'SELECT_ELEMENT':
      return { ...state, selectedBandId: action.bandId, selectedElementId: action.elementId };

    case 'DESELECT':
      return { ...state, selectedBandId: null, selectedElementId: null };

    case 'ADD_ELEMENT': {
      const next = {
        ...state,
        template: {
          ...state.template,
          bands: state.template.bands.map((b) =>
            b.id === action.bandId
              ? { ...b, elements: [...b.elements, action.element] }
              : b,
          ),
        },
        selectedBandId: action.bandId,
        selectedElementId: action.element.id,
      };
      return pushHistory(next);
    }

    case 'MOVE_ELEMENT': {
      const next = {
        ...state,
        template: {
          ...state.template,
          bands: state.template.bands.map((b) =>
            b.id === state.selectedBandId
              ? {
                  ...b,
                  elements: b.elements.map((el) =>
                    el.id === action.elementId
                      ? { ...el, x: action.x, y: action.y }
                      : el,
                  ),
                }
              : b,
          ),
        },
      };
      return next; // Don't push every move to history
    }

    case 'RESIZE_ELEMENT': {
      const next = {
        ...state,
        template: {
          ...state.template,
          bands: state.template.bands.map((b) =>
            b.id === state.selectedBandId
              ? {
                  ...b,
                  elements: b.elements.map((el) =>
                    el.id === action.elementId
                      ? { ...el, width: action.width, height: action.height }
                      : el,
                  ),
                }
              : b,
          ),
        },
      };
      return pushHistory(next);
    }

    case 'UPDATE_ELEMENT_PROPS': {
      const next = {
        ...state,
        template: {
          ...state.template,
          bands: state.template.bands.map((b) =>
            b.id === state.selectedBandId
              ? {
                  ...b,
                  elements: b.elements.map((el) =>
                    el.id === state.selectedElementId
                      ? { ...el, props: { ...el.props, ...action.props } }
                      : el,
                  ),
                }
              : b,
          ),
        },
      };
      return pushHistory(next);
    }

    case 'DELETE_ELEMENT': {
      const next = {
        ...state,
        template: {
          ...state.template,
          bands: state.template.bands.map((b) =>
            b.id === action.bandId
              ? { ...b, elements: b.elements.filter((el) => el.id !== action.elementId) }
              : b,
          ),
        },
        selectedBandId: null,
        selectedElementId: null,
      };
      return pushHistory(next);
    }

    case 'ADD_BAND': {
      const next = {
        ...state,
        template: {
          ...state.template,
          bands: [...state.template.bands, action.band],
        },
      };
      return pushHistory(next);
    }

    case 'REMOVE_BAND': {
      const next = {
        ...state,
        template: {
          ...state.template,
          bands: state.template.bands.filter((b) => b.id !== action.bandId),
        },
        selectedBandId: state.selectedBandId === action.bandId ? null : state.selectedBandId,
        selectedElementId: state.selectedBandId === action.bandId ? null : state.selectedElementId,
      };
      return pushHistory(next);
    }

    case 'RESIZE_BAND': {
      const next = {
        ...state,
        template: {
          ...state.template,
          bands: state.template.bands.map((b) =>
            b.id === action.bandId ? { ...b, height: action.height } : b,
          ),
        },
      };
      return next; // Don't push every resize
    }

    case 'UPDATE_TEMPLATE_META': {
      const next = {
        ...state,
        template: { ...state.template, ...action.meta },
      };
      return pushHistory(next);
    }

    case 'UNDO': {
      if (state.historyIndex <= 0) return state;
      const newIndex = state.historyIndex - 1;
      return {
        ...state,
        template: structuredClone(state.history[newIndex] ?? state.template),
        historyIndex: newIndex,
        selectedBandId: null,
        selectedElementId: null,
      };
    }

    case 'LOAD_TEMPLATE': {
      const t = action.template;
      return {
        template: t,
        selectedBandId: null,
        selectedElementId: null,
        history: [structuredClone(t)],
        historyIndex: 0,
      };
    }

    default:
      return state;
  }
}

// ── Component ────────────────────────────────────────────────

export interface TemplateDesignerProps {
  /** Initial template to load into designer */
  initialTemplate?: DesignTemplate;
  /** Called when user clicks Save */
  onSave?: (template: DesignTemplate) => void;
  /** Called when user clicks Preview */
  onPreview?: (template: DesignTemplate) => void;
  /** Show compact mode for embedded use */
  compact?: boolean;
}

export default function TemplateDesigner({
  initialTemplate,
  onSave,
  onPreview,
  compact = false,
}: TemplateDesignerProps) {
  const [state, dispatch] = useReducer(designerReducer, initialTemplate, (init) =>
    createInitialState(init),
  );

  const [leftCollapsed, setLeftCollapsed] = useState(false);
  const [rightCollapsed, setRightCollapsed] = useState(false);
  const [saving, setSaving] = useState(false);

  // ── Find selected element object ───────────────
  const selectedElement = useMemo(() => {
    if (!state.selectedBandId || !state.selectedElementId) return null;
    const band = state.template.bands.find((b) => b.id === state.selectedBandId);
    return band?.elements.find((el) => el.id === state.selectedElementId) ?? null;
  }, [state.template.bands, state.selectedBandId, state.selectedElementId]);

  // ── Handlers ───────────────────────────────────
  const handleSave = useCallback(async () => {
    if (!onSave) return;
    setSaving(true);
    try {
      await onSave(state.template);
    } finally {
      setSaving(false);
    }
  }, [onSave, state.template]);

  const handleUndo = useCallback(() => dispatch({ type: 'UNDO' }), []);

  const handleExportJSON = useCallback(() => {
    const json = JSON.stringify(state.template, null, 2);
    const blob = new Blob([json], { type: 'application/json' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `${state.template.name || 'template'}.json`;
    a.click();
    URL.revokeObjectURL(url);
  }, [state.template]);

  const handleImportJSON = useCallback(() => {
    const input = document.createElement('input');
    input.type = 'file';
    input.accept = '.json';
    input.onchange = async (e) => {
      const file = (e.target as HTMLInputElement).files?.[0];
      if (!file) return;
      const text = await file.text();
      try {
        const t = JSON.parse(text) as DesignTemplate;
        dispatch({ type: 'LOAD_TEMPLATE', template: t });
      } catch {
        alert('Invalid JSON file');
      }
    };
    input.click();
  }, []);

  const canUndo = state.historyIndex > 0;

  return (
    <div className={`flex flex-col bg-background ${compact ? '' : 'h-full'}`} style={compact ? {} : { height: 'calc(100vh - 60px)' }}>
      {/* Toolbar */}
      <div className="flex items-center gap-2 px-3 py-2 border-b border-border bg-muted/30 shrink-0">
        {/* Toggle panels */}
        <button
          onClick={() => setLeftCollapsed(!leftCollapsed)}
          className="p-1.5 rounded hover:bg-accent text-muted-foreground"
          title={leftCollapsed ? 'Show palette' : 'Hide palette'}
        >
          {leftCollapsed ? <PanelLeftOpen className="h-4 w-4" /> : <PanelLeftClose className="h-4 w-4" />}
        </button>

        <div className="w-px h-5 bg-border" />

        {/* Template name */}
        <FileText className="h-4 w-4 text-muted-foreground shrink-0" />
        <input
          type="text"
          className="bg-transparent border-none text-sm font-medium focus:outline-none focus:ring-0 w-48"
          value={state.template.name}
          onChange={(e) =>
            dispatch({ type: 'UPDATE_TEMPLATE_META', meta: { name: e.target.value } })
          }
          placeholder="Template Name"
        />

        {/* Page info */}
        <span className="text-[10px] text-muted-foreground bg-muted px-2 py-0.5 rounded">
          {state.template.pageSize} · {state.template.orientation}
        </span>

        <div className="flex-1" />

        {/* Import / Export */}
        <button
          onClick={handleImportJSON}
          className="flex items-center gap-1 px-2 py-1 text-xs rounded text-muted-foreground hover:bg-accent transition-colors"
          title="Import JSON"
        >
          <Upload className="h-3.5 w-3.5" />
          Import
        </button>

        <button
          onClick={handleExportJSON}
          className="flex items-center gap-1 px-2 py-1 text-xs rounded text-muted-foreground hover:bg-accent transition-colors"
          title="Export JSON"
        >
          <Download className="h-3.5 w-3.5" />
          Export
        </button>

        <div className="w-px h-5 bg-border" />

        {/* Undo */}
        <button
          onClick={handleUndo}
          disabled={!canUndo}
          className="p-1.5 rounded hover:bg-accent text-muted-foreground disabled:opacity-30 disabled:cursor-not-allowed"
          title="Undo"
        >
          <Undo2 className="h-4 w-4" />
        </button>

        {/* Preview */}
        {onPreview && (
          <button
            onClick={() => onPreview(state.template)}
            className="flex items-center gap-1 px-3 py-1.5 text-xs rounded border border-border hover:bg-accent transition-colors"
          >
            <Eye className="h-3.5 w-3.5" />
            Preview
          </button>
        )}

        {/* Save */}
        {onSave && (
          <button
            onClick={handleSave}
            disabled={saving}
            className="flex items-center gap-1 px-3 py-1.5 text-xs rounded bg-primary text-primary-foreground hover:bg-primary/90
                       disabled:opacity-50 transition-colors font-medium"
          >
            <Save className="h-3.5 w-3.5" />
            {saving ? 'Saving...' : 'Save'}
          </button>
        )}

        {/* Toggle right panel */}
        <button
          onClick={() => setRightCollapsed(!rightCollapsed)}
          className="p-1.5 rounded hover:bg-accent text-muted-foreground"
          title={rightCollapsed ? 'Show properties' : 'Hide properties'}
        >
          {rightCollapsed ? <PanelRightOpen className="h-4 w-4" /> : <PanelRightClose className="h-4 w-4" />}
        </button>
      </div>

      {/* Three-panel layout */}
      <div className="flex flex-1 overflow-hidden">
        {/* Left: Element Palette */}
        <ElementPalette collapsed={leftCollapsed} />

        {/* Center: Canvas */}
        <DesignCanvas
          template={state.template}
          selectedBandId={state.selectedBandId}
          selectedElementId={state.selectedElementId}
          onSelectElement={(bandId, elementId) =>
            dispatch({ type: 'SELECT_ELEMENT', bandId, elementId })
          }
          onDeselectAll={() => dispatch({ type: 'DESELECT' })}
          onAddElement={(bandId, element) =>
            dispatch({ type: 'ADD_ELEMENT', bandId, element })
          }
          onMoveElement={(bandId, elementId, x, y) =>
            dispatch({ type: 'MOVE_ELEMENT', elementId, x, y })
          }
          onResizeElement={(bandId, elementId, w, h) =>
            dispatch({ type: 'RESIZE_ELEMENT', elementId, width: w, height: h })
          }
          onDeleteElement={(bandId, elementId) =>
            dispatch({ type: 'DELETE_ELEMENT', bandId, elementId })
          }
          onResizeBand={(bandId, height) =>
            dispatch({ type: 'RESIZE_BAND', bandId, height })
          }
          onAddBand={(band) =>
            dispatch({ type: 'ADD_BAND', band })
          }
          onRemoveBand={(bandId) =>
            dispatch({ type: 'REMOVE_BAND', bandId })
          }
        />

        {/* Right: Property Panel */}
        <PropertyPanel
          template={state.template}
          selectedElement={selectedElement}
          selectedBandId={state.selectedBandId}
          collapsed={rightCollapsed}
          onUpdateElementProps={(props) =>
            dispatch({ type: 'UPDATE_ELEMENT_PROPS', props })
          }
          onUpdatePosition={(x, y, w, h) => {
            if (state.selectedElementId) {
              dispatch({ type: 'MOVE_ELEMENT', elementId: state.selectedElementId, x, y });
              dispatch({ type: 'RESIZE_ELEMENT', elementId: state.selectedElementId, width: w, height: h });
            }
          }}
          onDeleteElement={() => {
            if (state.selectedBandId && state.selectedElementId) {
              dispatch({
                type: 'DELETE_ELEMENT',
                bandId: state.selectedBandId,
                elementId: state.selectedElementId,
              });
            }
          }}
          onUpdateTemplateMeta={(meta) =>
            dispatch({ type: 'UPDATE_TEMPLATE_META', meta })
          }
        />
      </div>
    </div>
  );
}

// Re-export for convenience
export { type DesignTemplate } from './types';
